import { useState, useEffect } from "react";
import { useLocation, useParams, Link } from "wouter";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Save, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCreatePost, useUpdatePost } from "@workspace/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MediaManager } from "@/components/admin/MediaManager";

const formSchema = z.object({
  title: z.string().min(2),
  slug: z.string().min(2),
  excerpt: z.string().min(10),
  content: z.string().min(10),
  coverImage: z.string().optional(),
  category: z.string().min(2),
  tags: z.string(),
  published: z.boolean(),
  readTime: z.number().min(1)
});

export default function BlogEditor() {
  const { id } = useParams();
  const isEditing = id && id !== "new";
  const [, setLocation] = useLocation();

  const { data: post, isLoading } = useQuery({
    queryKey: ["/api/posts/id", id],
    queryFn: async () => {
      const res = await fetch(`/api/posts/id/${id}`);
      if (!res.ok) throw new Error("Failed to fetch post");
      return res.json();
    },
    enabled: !!isEditing && !!id
  });
  const queryClient = useQueryClient();
  const createPost = useCreatePost();
  const updatePost = useUpdatePost();
  const [coverUploading, setCoverUploading] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "", slug: "", excerpt: "", content: "", coverImage: "",
      category: "Web Development", tags: "", published: false, readTime: 5
    }
  });

  useEffect(() => {
    if (post && isEditing) {
      form.reset({
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt,
        content: post.content,
        coverImage: post.coverImage || "",
        category: post.category,
        tags: post.tags,
        published: post.published,
        readTime: post.readTime
      });
    }
  }, [post, isEditing, form]);

  const coverImage = useWatch({ control: form.control, name: "coverImage" });

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const { uploadToCloudinary } = await import("@/lib/uploadToCloudinary");
      const url = await uploadToCloudinary(file);
      form.setValue("coverImage", url, { shouldDirty: true, shouldTouch: true });
    } catch (err: any) {
      toast.error(err.message || "Failed to upload image");
    } finally {
      setCoverUploading(false);
      e.target.value = "";
    }
  };

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    const payload = {
      ...data,
      coverImage: data.coverImage || undefined,
      authorId: "1"
    };

    if (isEditing) {
      updatePost.mutate({ id: post?.id || "", data: payload }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["/api/posts/id", id] });
          setLocation("/ab-hq/blogs");
        }
      });
    } else {
      createPost.mutate({ data: payload }, {
        onSuccess: () => setLocation("/ab-hq/blogs")
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link href="/ab-hq/blogs"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <h1 className="font-display text-3xl font-bold">{isEditing ? "Edit Post" : "New Post"}</h1>
        </div>
      </div>

      <Tabs defaultValue="content" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="content">Post Content</TabsTrigger>
          <TabsTrigger value="media" disabled={!isEditing}>Photo Gallery</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="mt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="flex gap-6 items-start">

                {/* Main content column */}
                <div className="flex-1 min-w-0 space-y-6">
                  <div className="bg-card border border-border/50 rounded-xl p-6 space-y-6">
                    <FormField control={form.control} name="title" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl><Input className="text-lg" placeholder="Post title..." {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="slug" render={({ field }) => (
                      <FormItem>
                        <FormLabel>URL Slug</FormLabel>
                        <FormControl><Input placeholder="post-url-slug" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="excerpt" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Excerpt</FormLabel>
                        <FormControl><Textarea className="h-20 resize-none" placeholder="Short summary shown in post listings..." {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  <div className="bg-card border border-border/50 rounded-xl overflow-hidden">
                    <div className="px-6 pt-5 pb-2">
                      <p className="text-sm font-medium">Content</p>
                    </div>
                    <FormField control={form.control} name="content" render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="border-t border-border/50">
                            <RichTextEditor
                              theme="snow"
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Write your blog post content here..."
                              modules={{
                                toolbar: [
                                  [{ 'header': [1, 2, 3, false] }],
                                  ['bold', 'italic', 'underline', 'strike'],
                                  [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                  ['link', 'blockquote', 'code-block'],
                                  ['clean']
                                ],
                              }}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </div>

                {/* Settings sidebar */}
                <div className="w-72 shrink-0 space-y-4">
                  <div className="bg-card border border-border/50 rounded-xl p-5 space-y-4">
                    <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Publish</p>
                    <FormField control={form.control} name="published" render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <FormLabel className="cursor-pointer">Publish immediately</FormLabel>
                        <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                      </FormItem>
                    )} />
                    <Button type="submit" className="w-full" disabled={createPost.isPending || updatePost.isPending}>
                      <Save className="mr-2 h-4 w-4" />
                      {isEditing ? "Save Changes" : "Create Post"}
                    </Button>
                  </div>

                  <div className="bg-card border border-border/50 rounded-xl p-5 space-y-4">
                    <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Meta</p>
                    <FormField control={form.control} name="category" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <FormControl><Input {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="tags" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags</FormLabel>
                        <FormControl><Input placeholder="tag1, tag2, tag3" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="readTime" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Read Time (mins)</FormLabel>
                        <FormControl><Input type="number" {...field} onChange={e => field.onChange(parseInt(e.target.value))} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  <div className="bg-card border border-border/50 rounded-xl p-5 space-y-4">
                    <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Cover Image</p>
                    <FormField control={form.control} name="coverImage" render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div>
                            <input
                              id="cover-upload"
                              type="file"
                              accept="image/*"
                              className="hidden"
                              disabled={coverUploading}
                              onChange={handleCoverUpload}
                            />
                            {coverImage ? (
                              <div className="relative group rounded-lg overflow-hidden border border-border/50">
                                <img
                                  src={coverImage}
                                  alt="Cover preview"
                                  className="h-36 w-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                  <label
                                    htmlFor="cover-upload"
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs cursor-pointer transition-colors"
                                  >
                                    <Upload className="h-3.5 w-3.5" />
                                    Change
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => field.onChange("")}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/10 hover:bg-destructive/80 text-white text-xs transition-colors"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <label
                                htmlFor="cover-upload"
                                className={`flex flex-col items-center justify-center gap-2 h-36 w-full rounded-lg border-2 border-dashed border-border/50 hover:border-primary/50 hover:bg-accent/30 transition-all cursor-pointer ${coverUploading ? "opacity-50 cursor-not-allowed" : ""}`}
                              >
                                {coverUploading ? (
                                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                ) : (
                                  <>
                                    <Upload className="h-6 w-6 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">Click to upload cover image</span>
                                  </>
                                )}
                              </label>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </div>

              </div>
            </form>
          </Form>
        </TabsContent>

        <TabsContent value="media" className="mt-6">
          <div className="bg-card border border-border/50 rounded-xl p-8">
            <MediaManager type="POST" parentId={post?.id} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
